import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import {
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { FilterStates } from '@rocco/ui/filter-sort/view-models/filter-states';

/**
 * Check if a filter is active.
 * @param filterState The filter to check.
 * @returns The filter is active.
 */
export const isFilterStateActive = (filterState: FilterState): boolean => {
    if (isSingleValueFilterState(filterState)) {
        return filterState.checked;
    }

    if (isMultipleValuesFilterState(filterState)) {
        return filterState.selectedItems.length > 0;
    }

    if (isRangeFilterState(filterState) && filterState.value) {
        const hasMin =
            filterState.value.min !== undefined &&
            filterState.value.min !== 0 &&
            filterState.value.min !== filterState.range.min;
        const hasMax =
            filterState.value.max !== undefined &&
            filterState.value.max !== 0 &&
            filterState.value.max !== filterState.range.max;

        return hasMin || hasMax;
    }

    if (isLocationFilterState(filterState)) {
        return !!filterState.selectedLocation?.country;
    }

    return false;
};

/**
 * Get the number of active filters from the filter states.
 * @param filterStates The filter states.
 * @returns The number of active filters.
 */
export function getActiveFiltersCount(filterStates: FilterStates): number {
    const filtersCount = Object.values(filterStates.states).reduce(
        (count, state) => {
            if (!state) return count;

            return count + (isFilterStateActive(state) ? 1 : 0);
        },
        0,
    );

    const facetCount =
        filterStates.facet?.facetOptions?.reduce((count, facet) => {
            if (!facet) return count;

            return count + (isFilterStateActive(facet.state) ? 1 : 0);
        }, 0) ?? 0;

    return filtersCount + facetCount;
}
